import { ReactNode } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_API_KEY, hasGoogleMapsKey } from "../lib/googleMaps";

/**
 * Loads the Google Maps JS API once for the whole app.
 *
 * Mounted high so both the fleet map and the session path map share a single
 * script load and a single billed session. With no key configured it renders
 * children untouched — each map component then shows its own "not configured"
 * placeholder rather than the app failing to boot over a missing env var.
 */
export function GoogleMapsProvider({ children }: { children: ReactNode }) {
  if (!hasGoogleMapsKey) return <>{children}</>;
  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["maps", "geometry"]}>
      {children}
    </APIProvider>
  );
}

/**
 * Stand-in shown wherever a map would go when there's no API key. Says what to
 * do rather than rendering an empty grey box that reads as a bug.
 */
export function MapKeyMissing({ height }: { height: number | string }) {
  return (
    <div
      style={{
        height,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        textAlign: "center",
        padding: 16,
        background: "var(--color-background-secondary)",
        color: "var(--color-text-tertiary)",
        fontSize: 12,
        borderRadius: "var(--border-radius-md)",
      }}
    >
      <i className="ti ti-map-off" aria-hidden="true" style={{ fontSize: 20 }} />
      <span>Maps are not configured.</span>
      <span style={{ fontSize: 11 }}>
        Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code>.env</code> and
        restart the dev server.
      </span>
    </div>
  );
}
